// SeasonPage.js — /seasons/:slug (e.g. /seasons/2011-12): one Premier
// League season. The final table (or the table so far) with the champions
// and relegated clubs marked, the title race, the season's scoreline grid
// with its first-ever results ringed in gold, its biggest wins and
// highest-scoring matches, and how it compares with every other season.
// Data: the API's ?season=2011 (one season in the ?matches=1 shape, plus
// `firsts`, `seasons` and `cells`), computed with league.js.

import React, { useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Box, Skeleton } from "@mui/material";
import SEO from "./components/SEO";
import AdSlot from "./components/AdSlot";
import NotFoundPage from "./NotFoundPage";
import ScoreGrid from "./components/ScoreGrid";
import { MatchRows, Tile } from "./components/StatBits";
import { CtaRow, H2, InternalLink, P, PageHeader, ProsePage } from "./components/Prose";
import { BRAND } from "./theme";
import { formatDate } from "./components/heatmap/utils";
import { useSeededQuery } from "./components/scorelines/data";
import RaceChart, { LINE_COLORS } from "./components/clubs/RaceChart";
import LeagueTable, { LEAGUE_COLUMNS } from "./components/clubs/LeagueTable";
import { METRICS, parseMatches, raceFor, tableFor } from "./components/clubs/league";
import { POINT_DEDUCTIONS, clubInfo, possessive, scorelineKey, seasonText, slugify } from "./components/clubs/data";
import { listText, ordinal, parseSeasonSlug, relegationPlaces, seasonComplete, seasonPath, seasonQuery } from "./components/clubs/seasons";

const SITE_URL = "https://premierleaguescorigami.com";
const FIRST_SEASON = 1992;
const TABLE_COLUMNS = ["pos", "club", "matches", "won", "drawn", "lost", "gf", "ga", "gd", "pts"];

const name = (team) => clubInfo(team).name;
const short = (team) => clubInfo(team).short;
const kind = (hs, as) => (hs > as ? "home win" : hs < as ? "away win" : "draw");
const pct = (n, d) => `${Math.round((100 * n) / d)}%`;

export default function SeasonRoute() {
  const { slug } = useParams();
  const start = parseSeasonSlug(slug);
  if (start === null || start < FIRST_SEASON || start > new Date().getUTCFullYear()) return <NotFoundPage />;
  // Keyed by season, so nothing carries over between seasons.
  return <SeasonPage key={start} start={start} />;
}

function SeasonPage({ start }) {
  const label = seasonText(start);
  const resp = useSeededQuery(seasonQuery(start));
  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Seasons", item: `${SITE_URL}/seasons` },
      { "@type": "ListItem", position: 3, name: label, item: `${SITE_URL}${seasonPath(start)}` },
    ],
  };
  const header = <PageHeader eyebrow={<InternalLink to="/seasons">All seasons</InternalLink>} title={`Premier League ${label}`} />;

  if (!resp.data) {
    return (
      <ProsePage wide>
        <SEO
          title={`Premier League ${label} table, results and records`}
          description={`The Premier League ${label} season: the table, the title race, the scoreline grid and the season's biggest wins and first-ever results.`}
          path={seasonPath(start)}
          jsonLd={breadcrumbs}
        />
        {header}
        {resp.error ? (
          <P>This season couldn't be loaded right now. If it hasn't started yet, check back once it has.</P>
        ) : (
          Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} height={40} sx={{ bgcolor: "rgba(255,255,255,0.06)" }} />)
        )}
      </ProsePage>
    );
  }
  return <Season start={start} label={label} resp={resp.data} breadcrumbs={breadcrumbs} />;
}

function Season({ start, label, resp, breadcrumbs }) {
  const data = useMemo(() => parseMatches(resp), [resp]);
  const table = useMemo(() => tableFor(data, start, start), [data, start]);
  const race = useMemo(() => raceFor(data, start, start), [data, start]);
  const [sort, setSort] = useState({ key: "pos", desc: false });

  const matches = data.matches;
  const n = matches.length;
  const complete = seasonComplete(table.length, n);
  const expected = table.length * (table.length - 1);
  const goals = matches.reduce((s, m) => s + m.hs + m.as, 0);
  const gpg = goals / n;
  const homeWins = matches.filter((m) => m.hs > m.as).length;
  const draws = matches.filter((m) => m.hs === m.as).length;
  const awayWins = n - homeWins - draws;
  const [champ, runner] = table;
  const places = relegationPlaces(start);
  const bottom = table.slice(-places);
  const deductions = POINT_DEDUCTIONS.filter((d) => d.season === start);
  const team = (i) => data.teams[i].team;
  const fixture = (m) => `${short(team(m.h))} ${m.hs}–${m.as} ${short(team(m.a))}`;
  const row = (m, badge) => ({ date: formatDate(`${m.date}T00:00:00Z`), text: fixture(m), badge, to: `/scorelines/${scorelineKey(m.hs, m.as)}` });

  // How the title was won (or who leads).
  const margin = !runner
    ? ""
    : champ.pts > runner.pts
      ? `, ${champ.pts - runner.pts} point${champ.pts - runner.pts === 1 ? "" : "s"} ${complete ? "ahead of" : "clear of"} ${name(runner.team)}`
      : champ.gd > runner.gd
        ? `, level with ${name(runner.team)} but ahead on goal difference`
        : `, level with ${name(runner.team)} on points and goal difference but ahead on goals scored`;
  const lede = complete
    ? `${name(champ.team)} won the ${label} Premier League with ${champ.pts} points${margin}. ` +
      `${listText(bottom.map((t) => name(t.team)))} ${bottom.length === 1 ? "was" : "were"} relegated. ` +
      `The season's ${n.toLocaleString()} matches produced ${goals.toLocaleString()} goals, ${gpg.toFixed(2)} a game.`
    : `After ${n} of ${expected} matches, ${name(champ.team)} lead the ${label} Premier League with ${champ.pts} points${margin}. ` +
      `So far the season has produced ${goals.toLocaleString()} goals, ${gpg.toFixed(2)} a game.`;
  const seoTitle = `Premier League ${label} table, results and records`;
  const description = complete
    ? [
        `${name(champ.team)} won the ${label} Premier League with ${champ.pts} points. The final table, title race, scoreline grid, biggest wins and first-ever results.`,
        `${name(champ.team)} won the ${label} Premier League. The final table, title race, scoreline grid, biggest wins and first-ever results.`,
      ].find((d) => d.length <= 160) || `The ${label} Premier League final table, title race and the season's records.`
    : `The ${label} Premier League table so far, the title race, and the season's scoreline grid, biggest wins and first-ever results, updated after every match.`;

  // The season's scoreline grid (home goals across, away goals up), with
  // results the league had never seen before ringed in gold.
  const counts = new Map();
  for (const m of matches) counts.set(`${m.hs}-${m.as}`, (counts.get(`${m.hs}-${m.as}`) || 0) + 1);
  const maxGoals = Math.max(6, ...matches.map((m) => Math.max(m.hs, m.as)));
  const firsts = resp.firsts.map(([date, h, a, hs, as]) => ({ date, h, a, hs, as }));
  const gold = new Set(firsts.map((m) => `${m.hs}-${m.as}`));

  const byMargin = [...matches].sort((a, b) => Math.abs(b.hs - b.as) - Math.abs(a.hs - a.as) || b.hs + b.as - (a.hs + a.as) || a.date.localeCompare(b.date));
  const byGoals = [...matches].sort((a, b) => b.hs + b.as - (a.hs + a.as) || Math.abs(b.hs - b.as) - Math.abs(a.hs - a.as) || a.date.localeCompare(b.date));
  const common = [...counts].sort((a, b) => b[1] - a[1]).slice(0, 5);

  // Against every completed season (the API's per-season totals; every
  // season before the latest is complete, and 20 clubs play 380 matches).
  const latest = resp.seasons[resp.seasons.length - 1][0];
  const completed = resp.seasons.filter(([s, played]) => s < latest || played >= 380);
  const rateOf = ([, played, g]) => g / played;
  const allGoals = completed.reduce((s, r) => s + r[2], 0);
  const allMatches = completed.reduce((s, r) => s + r[1], 0);
  const allHome = completed.reduce((s, r) => s + r[3], 0);
  const rank = complete ? [...completed].sort((a, b) => rateOf(b) - rateOf(a)).findIndex(([s]) => s === start) + 1 : 0;
  const context = [
    complete
      ? `At ${gpg.toFixed(2)} goals a game, ${label} was ${
          rank === 1 ? "the highest-scoring" : rank === completed.length ? "the lowest-scoring" : `the ${ordinal(rank)} highest-scoring`
        } of the ${completed.length} completed Premier League seasons (the average is ${(allGoals / allMatches).toFixed(2)}).`
      : `So far ${label} is averaging ${gpg.toFixed(2)} goals a game, against ${(allGoals / allMatches).toFixed(2)} across the ${completed.length} completed seasons.`,
    `Home sides won ${pct(homeWins, n)} of matches, against ${pct(allHome, allMatches)} across all seasons; ${pct(draws, n)} were draws and ${pct(awayWins, n)} away wins.`,
  ].join(" ");

  const col = LEAGUE_COLUMNS.find((c) => c.key === sort.key);
  const rows = [...table].sort((a, b) => {
    const va = col.sort(a);
    const vb = col.sort(b);
    const cmp = col.text ? String(va).localeCompare(String(vb)) : va - vb;
    return (sort.desc ? -cmp : cmp) || a.pos - b.pos;
  });
  const onSort = (c) => setSort((s) => (s.key === c.key ? { key: c.key, desc: !s.desc } : { key: c.key, desc: !(c.text || c.asc) }));
  const relegated = new Set(bottom.map((t) => t.team));
  const zoneFor = (t) => (t.pos === 1 && complete ? "champion" : relegated.has(t.team) ? "relegated" : null);

  const top = table.slice(0, 4).map((t) => t.idx);
  const pts = METRICS.find((m) => m.key === "pts");
  const range = { from: start, to: start };
  const isFirstSeason = start === FIRST_SEASON;

  return (
    <ProsePage wide>
      <SEO title={seoTitle} description={description} path={seasonPath(start)} jsonLd={breadcrumbs} />
      <Box sx={{ maxWidth: 820 }}>
        <PageHeader eyebrow={<InternalLink to="/seasons">All seasons</InternalLink>} title={`Premier League ${label}`} lede={lede} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: 1.25, mb: 1, maxWidth: 820 }}>
        <Tile value={short(champ.team)} label={complete ? "Champions" : "Leaders"} />
        <Tile value={goals.toLocaleString()} label="Goals" />
        <Tile value={gpg.toFixed(2)} label="Goals a game" />
        <Tile value={pct(homeWins, n)} label="Home wins" />
      </Box>

      <H2 id="table">{complete ? `Final table` : `Table so far`}</H2>
      <LeagueTable rows={rows} sort={sort} onSort={onSort} columns={TABLE_COLUMNS} zoneFor={zoneFor} minWidth={620} />
      <Box component="p" sx={{ color: BRAND.textSecondary, fontSize: 13.5, lineHeight: 1.7, maxWidth: 820, mt: 1, mb: 3 }}>
        {complete ? (
          <>
            <Box component="span" sx={{ color: BRAND.cyan, fontWeight: 800 }}>
              ▌
            </Box>{" "}
            Champions{" "}
            <Box component="span" sx={{ color: "#ff6b6b", fontWeight: 800, ml: 1 }}>
              ▌
            </Box>{" "}
            Relegated.{" "}
          </>
        ) : (
          <>
            <Box component="span" sx={{ color: "#ff6b6b", fontWeight: 800 }}>
              ▌
            </Box>{" "}
            Relegation places.{" "}
          </>
        )}
        Three points for a win, one for a draw
        {deductions.length
          ? `, minus ${listText(deductions.map((d) => `${possessive(name(d.team))} ${d.points}-point deduction (${d.reason})`))}, marked *.`
          : "."}{" "}
        <InternalLink to={`/all-time-table?${new URLSearchParams(range)}`}>Sort and filter it on the all-time table →</InternalLink>
      </Box>

      <H2 id="race">{complete ? `The ${label} title race` : `The ${label} title race so far`}</H2>
      <P>
        The top four's points, match day by match day.{" "}
        <InternalLink to={`/race?${new URLSearchParams({ ...range, clubs: table.slice(0, 4).map((t) => clubInfo(t.team).slug).join(",") })}`}>
          Pick other clubs or measures on the race chart →
        </InternalLink>
      </P>
      {race.days.length > 0 && (
        <RaceChart
          race={race}
          teams={data.teams}
          selected={top}
          colors={LINE_COLORS}
          metric={pts}
          viewKey={`season-${start}`}
          video={{ range: label, title: `The ${label} title race`, fileName: slugify(`premier league ${label} title race`) }}
        />
      )}

      <Box sx={{ maxWidth: 820 }}>
        <H2 id="grid">The {label} scoreline grid</H2>
        <P>
          Every result of the season, home goals along the bottom and away goals up the side. Each square counts the matches
          that finished that way{gold.size > 0 && !isFirstSeason ? "; squares ringed in gold are results the Premier League had never seen before this season" : ""}.
          Select a square for that scoreline's page.
        </P>
      </Box>
      <ScoreGrid
        counts={counts}
        maxGoals={maxGoals}
        xLabel="Home goals"
        yLabel="Away goals"
        gold={isFirstSeason ? null : gold}
        labelFor={(hs, as, c) => `${hs}–${as} ${kind(hs, as)}: ${c} match${c === 1 ? "" : "es"} in ${label}`}
      />

      <Box sx={{ maxWidth: 820 }}>
        <H2 id="firsts">First-ever results</H2>
        {isFirstSeason ? (
          <P>
            As the first Premier League season, every one of its {counts.size} different results was new to the league.
          </P>
        ) : firsts.length ? (
          <>
            <P>
              {firsts.length === 1 ? "One result" : `${firsts.length} results`} in {label} had never happened in the Premier League
              before, counting home and away separately:
            </P>
            <MatchRows rows={firsts.map((m) => row(m, `First ${m.hs > m.as ? `${m.hs}–${m.as}` : `${m.as}–${m.hs}`} ${kind(m.hs, m.as)}`))} />
          </>
        ) : (
          <P>
            {complete ? `Every result in ${label} had happened before.` : `No result so far in ${label} has been new to the Premier League.`}{" "}
            <InternalLink to="/watch">See the odds of one in the next round →</InternalLink>
          </P>
        )}

        <H2 id="biggest-wins">Biggest wins</H2>
        <MatchRows rows={byMargin.slice(0, 5).map((m) => row(m, `${Math.abs(m.hs - m.as)}-goal ${m.hs > m.as ? "home" : "away"} win`))} />
        <H2 id="highest-scoring">Highest-scoring matches</H2>
        <MatchRows rows={byGoals.slice(0, 5).map((m) => row(m, `${m.hs + m.as} goals`))} />

        <AdSlot position="article" />

        <H2 id="most-common">Most common results</H2>
        <MatchRows
          rows={common.map(([key, c], i) => {
            const [hs, as] = key.split("-").map(Number);
            return { date: `#${i + 1}`, text: `${hs}–${as} ${kind(hs, as)}`, badge: `${c} · ${pct(c, n)}`, to: `/scorelines/${scorelineKey(hs, as)}` };
          })}
        />

        <H2 id="compared">How {label} compares</H2>
        <P>{context}</P>

        <SeasonNav start={start} seasons={resp.seasons.map(([s]) => s)} />
      </Box>

      <CtaRow
        links={[
          { to: "/seasons", label: "Every season", primary: true },
          { to: `/all-time-table?${new URLSearchParams(range)}`, label: `${label} table` },
          { to: "/race", label: "Race chart" },
        ]}
      />
    </ProsePage>
  );
}

function SeasonNav({ start, seasons }) {
  const i = seasons.indexOf(start);
  const prev = seasons[i - 1];
  const next = seasons[i + 1];
  const link = (s, text) => (
    <Box
      component={RouterLink}
      to={seasonPath(s)}
      sx={{ color: BRAND.cyan, fontWeight: 700, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
    >
      {text}
    </Box>
  );
  return (
    <Box component="nav" aria-label="Other seasons" sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 3, fontSize: 15 }}>
      <span>{prev !== undefined && link(prev, `← ${seasonText(prev)}`)}</span>
      <span>{next !== undefined && link(next, `${seasonText(next)} →`)}</span>
    </Box>
  );
}
